import { Button, Stack, Typography } from '@mui/material'
import { Icons, SkeletonText } from 'utoniq-ui'

import { useBrowserLink } from '@/components/lib/BrowserLink'
import { EventCategory, EventStatusTag } from '@/components/organisms'
import { State } from '@/components/pages/ConsoleEventDetail/template'
import { styleSets } from '@/components/styles'
import { config } from '@/config'
import { useTranslation } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { Cell, CellFrame } from '@/core/architecture'
import { LinkifyBox } from '@/lib/reactLinkify'

type ConsoleEventDetailProps = {
  loading: boolean
  event: State['event']
  preSales: State['preSale']
  onOpenMap: () => void
}
export const ConsoleEventDetail: Cell<ConsoleEventDetailProps> = ({
  loading,
  event,
  preSales,
  onOpenMap,
}) => {
  const { t } = useTranslation('consoleEventDetail')
  const { onClick: openMap } = useBrowserLink(event?.location?.mapUri ?? '')
  const { analytics } = useAnalytics()

  if (loading) {
    return (
      <CellFrame sx={{ p: 5 }}>
        <SkeletonText variant="h3" />
        <Stack sx={{ pt: 4 }}>
          <SkeletonText variant="small" numberOfLine={4} />
        </Stack>
        <Stack sx={{ pt: 4 }}>
          <SkeletonText variant="small" width="25%" />
          <SkeletonText variant="small" width="100%" />
        </Stack>
        <Stack sx={{ pt: 4 }}>
          <SkeletonText variant="small" width="25%" />
          <SkeletonText variant="small" width="100%" />
        </Stack>
      </CellFrame>
    )
  }
  return (
    <CellFrame sx={{ p: 5 }}>
      <Typography variant="h3">{event?.title}</Typography>
      {event && (
        <Stack direction="row" spacing={2} alignItems="center" pt={3}>
          <EventCategory event={{ ...event, preSales } as never} />
          <EventStatusTag status={event.publishStatus} />
          {!!event.formattedStartedAt && (
            <Typography variant="caption" color="text.secondary">
              {t('publishedAt', { date: event.formattedStartedAt })}
            </Typography>
          )}
        </Stack>
      )}
      <LinkifyBox>
        <Typography variant="body2" sx={{ pt: 3 }}>
          {event?.description}
        </Typography>
      </LinkifyBox>
      {!!event?.dateRange && (
        <Stack sx={{ pt: 3 }} spacing={1}>
          <Typography variant="subtitle1">{t('eventDate')}</Typography>
          <Typography variant="body2">{event?.dateRange}</Typography>
        </Stack>
      )}
      {!!event?.formattedEventTime && (
        <Stack sx={{ pt: 3 }} spacing={1}>
          <Typography variant="subtitle1">{t('eventTime')}</Typography>
          <Typography variant="body2">{event?.formattedEventTime}</Typography>
        </Stack>
      )}
      {event?.location && (
        <Stack sx={{ pt: 3 }} spacing={1}>
          <Typography variant="subtitle1">{t('venue')}</Typography>
          <Stack direction="row" alignItems="center">
            <Button
              variant="text"
              size="small"
              onClick={() => {
                analytics?.track('click_eventMap', {
                  applicationKey: config.applicationKey,
                  eventId: event?.id,
                  type: 'consoleEventDetail',
                })
                onOpenMap()
              }}
              sx={{ pl: 0, ml: -2 }}
            >
              <Icons.Pin fontSize="small" sx={{ color: 'primary.main' }} />
              <Typography
                variant="body2"
                color="primary.main"
                align="left"
                sx={{ ...styleSets.lineCamp(1) }}
              >
                {event?.location?.approximateAddress}
              </Typography>
            </Button>
            {!!event?.location?.mapUri && (
              <Stack
                direction="row"
                onClick={() => {
                  analytics?.track('click_eventMapLink', {
                    applicationKey: config.applicationKey,
                    eventId: event?.id,
                    type: 'consoleEventDetail',
                  })
                  openMap()
                }}
              >
                <Typography
                  component="span"
                  variant="subtitle1"
                  display="inherit"
                  minWidth={90}
                >
                  （
                  <Typography
                    component="span"
                    sx={{ cursor: 'pointer' }}
                    variant="subtitle1"
                    color="primary"
                  >
                    {t('venueMap', '会場MAP')}
                  </Typography>
                  ）
                </Typography>
              </Stack>
            )}
          </Stack>
        </Stack>
      )}
    </CellFrame>
  )
}
